import { useEffect, useMemo, useRef } from "react";
import type { useStepValidation } from "../hooks";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import {
  WHAT_YOULL_NEED,
  WHAT_HAPPENS_AFTER_SUBMIT_INTRO,
  WHAT_HAPPENS_AFTER_SUBMIT,
  IMPORTANT_NOTES,
} from "../content/welcomeContent";

const GLOBE_URL = "/vertical-optimyl-logo-white-text.svg";

interface StepWelcomeProps {
  validation: ReturnType<typeof useStepValidation>;
}

export function StepWelcome(_props: StepWelcomeProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const setWelcomeScrollComplete = useUiStore((s) => s.setWelcomeScrollComplete);
  const uwMethod = useFormStore((s) => s.meta.uwMethod);
  const whatYoullNeed = useMemo(
    () => WHAT_YOULL_NEED.filter((item) => !(item.hideForClaims && uwMethod === "Claims")),
    [uwMethod],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setWelcomeScrollComplete(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setWelcomeScrollComplete(true); observer.disconnect(); } },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [setWelcomeScrollComplete]);

  return (
    <>
      {/* Hero */}
      <div className="rounded-[14px] overflow-hidden mb-6">
        <div className="flex flex-col items-center text-center px-8 pb-10 pt-6 relative overflow-hidden bg-brand-navy">
          <div className="absolute rounded-full bg-brand-green-10 w-[300px] h-[300px] -top-[120px] -right-[80px]" />
          <div className="absolute rounded-full bg-brand-green-06 w-[220px] h-[220px] -bottom-[90px] -left-[60px]" />
          <img
            src={GLOBE_URL}
            alt="Optimyl"
            className="relative z-10 w-[140px] sm:w-[220px] md:w-[280px] h-auto mb-8 mt-4"
          />
          <h1 className="relative z-10 text-white font-black text-2xl sm:text-3xl leading-tight mt-0 mb-2">
            Thank You for Choosing<br />Optimyl Benefits
          </h1>
        </div>
        <div className="px-8 py-4 text-center border-t-4 bg-brand-green-subtle border-brand-green">
          <p className="text-lg font-bold uppercase tracking-wide text-brand-navy m-0">
            Please review the instructions below before completing your intake form
          </p>
          <p className="text-xs font-medium italic text-gray-500 mt-1.5 mb-0">
            Click '<strong>Instructions &amp; Info</strong>' icon at the top right of page to see these details while in the application.
          </p>
        </div>
      </div>

      {/* What you'll need */}
      <div className="bg-white rounded-xl border border-gray-200 px-8 py-6 mb-5">
        <h2 className="text-base font-extrabold text-brand-navy mt-0 mb-4 pb-2 border-b-2 border-brand-green">
          What You'll Need to Complete This Form
        </h2>
        <ul className="list-none p-0 m-0">
          {whatYoullNeed.map((item, i, arr) => (
            <li
              key={i}
              className={`relative pl-4 py-2.5 text-sm text-gray-700 leading-relaxed${i < arr.length - 1 ? " border-b border-gray-100" : ""}`}
            >
              <span className="absolute left-0 top-[1.05rem] w-1 h-1 rounded-full bg-brand-green" />
              {item.content}
            </li>
          ))}
        </ul>
      </div>

      {/* What happens after */}
      <div className="bg-white rounded-xl border border-gray-200 px-8 py-6 mb-5">
        <h2 className="text-base font-extrabold text-brand-navy mt-0 mb-4 pb-2 border-b-2 border-brand-green">
          What Happens After You Submit
        </h2>
        <p className="text-sm text-gray-500 mb-4 leading-relaxed">{WHAT_HAPPENS_AFTER_SUBMIT_INTRO}</p>
        <ol className="list-none p-0 m-0">
          {WHAT_HAPPENS_AFTER_SUBMIT.map((item, i, arr) => (
            <li
              key={i}
              className={`flex gap-3 py-2.5 text-sm text-gray-700 leading-relaxed${i < arr.length - 1 ? " border-b border-gray-100" : ""}`}
            >
              <span className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5 bg-brand-navy">
                {i + 1}
              </span>
              <div><strong>{item.label}</strong> {item.detail}</div>
            </li>
          ))}
        </ol>
      </div>

      {/* Important notes */}
      <div className="bg-white rounded-xl border border-gray-200 px-8 py-6 mb-5">
        <h2 className="text-base font-extrabold text-brand-navy mt-0 mb-4 pb-2 border-b-2 border-brand-green">
          A Few Important Notes
        </h2>
        <ul className="list-none p-0 m-0">
          {IMPORTANT_NOTES.map((item, i, arr) => (
            <li
              key={i}
              className={`relative pl-4 py-2.5 text-sm text-gray-700 leading-relaxed${i < arr.length - 1 ? " border-b border-gray-100" : ""}`}
            >
              <span className="absolute left-0 top-[1.05rem] w-1 h-1 rounded-full bg-brand-green" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <p className="text-center text-sm text-gray-400 mt-5 pt-3 border-t border-gray-200">
        We look forward to supporting you and your team.
      </p>
      <div ref={sentinelRef} aria-hidden="true" />
    </>
  );
}
